/**
 * Marker + copy helpers for `deck.details.combos.profile`. Mirrors
 * `deckSynergyProfileMarkers.js` and targets the combo-profile shape
 * described in api-changes-combos.md. Data-only so UI modules stay thin.
 *
 * The backend ships opaque ids in `strengths` / `risks` / `notable`; the
 * UI maps them to user-facing copy. Unknown ids no-op (render a humanised
 * fallback) so a new id appearing from the backend does not break the UI.
 */

// ---- Enum label maps --------------------------------------------------

const DENSITY_LABELS = {
    none: 'None',
    light: 'Light',
    moderate: 'Moderate',
    dense: 'Dense',
};

const MAGNITUDE_LABELS = {
    none: 'None',
    minor: 'Minor',
    notable: 'Notable',
    major: 'Major',
};

const RELIABILITY_LABELS = {
    fragile: 'Fragile',
    conditional: 'Conditional',
    stable: 'Stable',
    redundant: 'Redundant',
};

const WINCON_ISOLATION_LABELS = {
    none: 'No wincons',
    single: 'Single line',
    clustered: 'Clustered',
    diverse: 'Diverse',
};

export const COMBO_ENUM_LABELS = {
    density: DENSITY_LABELS,
    redundancy: RELIABILITY_LABELS,
    deploymentBand: DENSITY_LABELS,
    cleanLineDensity: DENSITY_LABELS,
    friction: DENSITY_LABELS,
    magnitude: MAGNITUDE_LABELS,
    winconLineIsolation: WINCON_ISOLATION_LABELS,
};

function humanizeId(id) {
    if (!id) return '';
    return id
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}

export function comboEnumLabel(field, value) {
    if (!value) return null;
    const map = COMBO_ENUM_LABELS[field];
    return map?.[value] || humanizeId(value);
}

// ---- Shape-tag copy (glanceable hero/facet chips) --------------------
// `comboEnumLabel` returns the bare enum value ("Dense", "Single line"),
// which reads badly once the UI tacks a dimension word after it ("Dense
// density", "Single line wincon lines"). These are complete, self-
// contained phrases for the hero shape tags and facet headlines so each
// chip stands on its own. `deploymentBand` notably reuses the density
// enum (light/moderate/dense) but actually describes deploy SPEED, so
// it gets speed-flavored copy rather than "dense deployment".
const SHAPE_TAG_LABELS = {
    density: {
        light: 'Light combo package',
        moderate: 'Moderate combo package',
        dense: 'Dense combo package',
    },
    redundancy: {
        fragile: 'Fragile backups',
        conditional: 'Conditional backups',
        stable: 'Stable backups',
        redundant: 'Redundant backups',
    },
    deploymentBand: {
        light: 'Slow to deploy',
        moderate: 'Moderate deploy speed',
        dense: 'Quick to deploy',
    },
    winconLineIsolation: {
        none: 'No wincon lines',
        single: 'Single wincon line',
        clustered: 'Clustered wincons',
        diverse: 'Diverse wincons',
    },
};

export function comboShapeTagLabel(field, value) {
    if (!value) return null;
    return SHAPE_TAG_LABELS[field]?.[value] || comboEnumLabel(field, value);
}

// ---- Outcome bucket labels -------------------------------------------

export const OUTCOME_LABELS = {
    infiniteMana: 'Infinite mana',
    infiniteDraw: 'Infinite draw',
    infiniteDamage: 'Infinite damage',
    infiniteCombat: 'Infinite combat',
    infiniteTurns: 'Infinite turns',
    infiniteTokens: 'Infinite tokens',
    infiniteMill: 'Mill out opponents',
    infiniteExile: 'Exile opponents',
    infiniteCast: 'Storm / infinite casts',
    infiniteETB: 'Infinite ETB / flicker',
    infiniteUntap: 'Infinite untap',
    infiniteLifegain: 'Infinite lifegain',
    infiniteLifeloss: 'Infinite lifeloss',
    infiniteCounters: 'Infinite counters',
    infiniteLandfall: 'Infinite landfall',
    infiniteLoot: 'Infinite loot',
    infiniteScry: 'Infinite scry',
    opponentsLocked: 'Opponents locked',
    winTheGame: 'Autowin',
    exileYourLibrary: 'Exile your library',
    libraryOntoBattlefield: 'Put library onto battlefield',
    other: 'Other effects',
};

export function outcomeLabel(key) {
    return OUTCOME_LABELS[key] || humanizeId(key);
}

// ---- Strengths / risks / notable markers -----------------------------

const asInt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : null;
};

const asPct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return `${Math.round(n * 100)}%`;
};

const asDecimal = (v, digits = 2) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(digits) : null;
};

const joinOutcomes = (list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map(outcomeLabel).join(' · ');
};

// Each entry returns a concrete { label, detail } given the data payload.
// `label` is the headline chip text; `detail` is a tooltip-friendly hint.
const STRENGTH_META = {
    hasWincon: {
        tone: 'alert',
        build: (d) => ({
            label: d.winconCount > 1 ? `${d.winconCount} wincon combos` : 'Closes the game',
            detail: 'At least one combo closes the game on its own.',
        }),
    },
    multipleWincons: {
        tone: 'alert',
        build: (d) => ({
            label: `${asInt(d.winconCount)} wincon combos`,
            detail: 'Multiple combos can close the game independently.',
        }),
    },
    redundantWinconLines: {
        tone: 'alert',
        build: (d) => ({
            label: `${asInt(d.effectiveWinconLines)} distinct wincon lines`,
            detail: `${asInt(d.winconCount)} wincon combos split across ${asInt(
                d.effectiveWinconLines
            )} independent engines.`,
        }),
    },
    twoCardWincon: {
        tone: 'alert',
        build: (d) => ({
            label: d.twoCardLineCount > 1
                ? `${asInt(d.twoCardLineCount)} two-card lines`
                : 'Two-card wincon',
            detail: 'Wins with as few as two cards.',
        }),
    },
    cleanTwoCardLine: {
        tone: 'alert',
        build: (d) => ({
            label: d.cleanLineCount > 1
                ? `${asInt(d.cleanLineCount)} clean two-card lines`
                : 'Clean two-card line',
            detail: `No board state, no keywords, minimal extra mana${
                d.averageBias != null ? ` (avg bias ${asDecimal(d.averageBias)})` : ''
            }.`,
        }),
    },
    manaSinkBackbone: {
        tone: 'alert',
        build: (d) => ({
            label: 'Mana sink + infinite mana',
            detail: `${asInt(d.manaSinkCount)} sinks fed by ${asInt(d.infiniteManaCount)} infinite-mana combos.`,
        }),
    },
    deterministicOutcome: {
        tone: 'alert',
        build: (d) => {
            const bits = [];
            if (d.winGameCount) bits.push(`${asInt(d.winGameCount)} autowin`);
            if (d.putAllFromLibraryCount)
                bits.push(`${asInt(d.putAllFromLibraryCount)} library-onto-battlefield`);
            return {
                label: 'Deterministic finish',
                detail: bits.length ? bits.join(' · ') : 'Ends the game on resolution.',
            };
        },
    },
    lowManaThreshold: {
        tone: 'alert',
        build: (d) => ({
            label: `Goes off at ${asInt(d.minWinconManaCost)} mana`,
            detail: 'Cheapest wincon can deploy early.',
        }),
    },
    versatilePieces: {
        tone: 'ok',
        build: (d) => ({
            label: `${asInt(d.versatilePieceCount)} versatile pieces`,
            detail: `Cards pulling double duty${
                d.avgCombosPerCard != null
                    ? ` (avg ${asDecimal(d.avgCombosPerCard, 1)} combos / card)`
                    : ''
            }.`,
        }),
    },
    commanderEnablesLines: {
        tone: 'alert',
        build: (d) => ({
            label: 'Commander enables combos',
            detail: `${asPct(d.commanderPieceShare)} of combos include a commander.`,
        }),
    },
    diverseOutcomeMix: {
        tone: 'alert',
        build: (d) => ({
            label: `${Array.isArray(d.outcomes) ? d.outcomes.length : 0} wincon paths`,
            detail: joinOutcomes(d.outcomes) || 'Multiple distinct wincon effects.',
        }),
    },
    stableRedundancy: {
        tone: 'ok',
        build: (d) => ({
            label: `${comboEnumLabel('redundancy', d.redundancy) || 'Stable'} backups`,
            detail: `${asInt(d.count)} combos across ${asInt(d.effectiveLines)} lines.`,
        }),
    },
    enablersFeedWincons: {
        tone: 'ok',
        build: (d) => ({
            label: `${asInt(d.enablersFeedingWincons)} enablers feed wincons`,
            detail: 'Enabler combos share cards with finishers.',
        }),
    },
};

const RISK_META = {
    noWinconLines: {
        tone: 'warn',
        build: (d) => ({
            label: 'No wincon combos',
            detail: `${asInt(d.count)} combos, ${asInt(d.enablerCount)} enablers — none close the game.`,
        }),
    },
    allLinesConverge: {
        tone: 'warn',
        build: (d) => ({
            label: 'All lines converge',
            detail: `${asInt(d.count)} combos collapse into ${asInt(d.effectiveLines)} engine.`,
        }),
    },
    highManaThreshold: {
        tone: 'warn',
        build: (d) => ({
            label: `Wincon costs ${asInt(d.minWinconManaCost)} mana`,
            detail: 'Combo deploys late.',
        }),
    },
    boardDependent: {
        tone: 'warn',
        build: (d) => ({
            label: 'Needs board state',
            detail: `${asInt(d.winconBoardDependentCount)} of ${asInt(d.winconCount)} wincons require pieces already in play.`,
        }),
    },
    fragileRequirements: {
        tone: 'alert',
        build: (d) => ({
            label: 'Fragile requirements',
            detail: `Avg ${asDecimal(d.averageQualifiersPerCombo, 1)} qualifiers per combo.`,
        }),
    },
    keywordGated: {
        tone: 'warn',
        build: (d) => ({
            label: `${asInt(d.keywordDependentCount)} keyword-gated`,
            detail: 'Requires a specific keyword (deathtouch, haste, etc.) to function.',
        }),
    },
    commanderLocked: {
        tone: 'warn',
        build: (d) => ({
            label: 'Commander-locked',
            detail: `All ${asInt(d.commanderAnchoredCount)} combos need a specific commander in play.`,
        }),
    },
    enablerHeavy: {
        tone: 'warn',
        build: (d) => ({
            label: 'Enabler-heavy, no finisher',
            detail: `${asInt(d.enablerCount)} enabler combos, 0 wincons (${asInt(d.count)} total).`,
        }),
    },
    lineOverlapDiscount: {
        tone: 'warn',
        build: (d) => ({
            label: 'Overlapping lines',
            detail: `${asInt(d.count)} combos collapse to ${asInt(d.effectiveLines)} distinct lines.`,
        }),
    },
    singleCardAnchor: {
        tone: 'warn',
        build: (d) => ({
            label: 'Single card anchor',
            detail: `One card appears in ${asInt(d.anchorComboCount)} of ${asInt(d.totalCombos)} combos.`,
        }),
    },
    noCleanLine: {
        tone: 'warn',
        build: (d) => ({
            label: 'No clean line',
            detail: `No combo meets the cheap / unconditional bar${
                d.averageBias != null ? ` (avg bias ${asDecimal(d.averageBias)})` : ''
            }.`,
        }),
    },
    lifeCostHeavy: {
        tone: 'warn',
        build: (d) => ({
            label: `${asInt(d.count)} pay-life lines`,
            detail: 'Multiple combos taxed by life payments.',
        }),
    },
    manaTaxHeavy: {
        tone: 'warn',
        build: (d) => ({
            label: `${asInt(d.count)} mana-tax lines`,
            detail: 'Loops carry an ongoing mana cost.',
        }),
    },
};

const NOTABLE_META = {
    twoCardLinePresent: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} two-card combos` : 'Two-card combo',
            detail: 'Pod-disclosure worth flagging.',
        }),
    },
    extraTurnsCombo: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} extra-turn combos` : 'Extra-turn combo',
            detail: 'Combo produces infinite / repeatable turns.',
        }),
    },
    opponentsLocked: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} lock combos` : 'Lock combo',
            detail: 'Combo locks opponents out of the game.',
        }),
    },
    stormOrCast: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} storm lines` : 'Storm / infinite cast',
            detail: 'Combo chains infinite casts.',
        }),
    },
    autowinPresent: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} autowin combos` : 'Autowin present',
            detail: 'Explicit "win the game" effect present.',
        }),
    },
    templateExpansion: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} template matches` : 'Template-matched combo',
            detail: 'Detected via Scryfall template expansion rather than an exact Spellbook entry.',
        }),
    },
    manaSinkPresent: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} mana sinks` : 'Mana sink',
            detail: 'Combo consumes infinite mana.',
        }),
    },
    manaDenialCombo: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} mana-denial lines` : 'Mana-denial combo',
            detail: 'Combo denies opponents access to mana.',
        }),
    },
    detectedHeuristically: {
        tone: 'cool',
        build: (d) => ({
            label: d.count > 1 ? `${asInt(d.count)} heuristic matches` : 'Heuristic match',
            detail: 'Combo detected by pattern matching rather than an exact entry.',
        }),
    },
};

const UNKNOWN_META = {
    tone: 'cool',
    build: (d, id) => ({ label: humanizeId(id), detail: null }),
};

function mapMarkers(markers, meta) {
    if (!Array.isArray(markers) || markers.length === 0) return [];
    return markers
        .map((marker) => {
            if (!marker || !marker.id) return null;
            const entry = meta[marker.id] || UNKNOWN_META;
            const data = marker.data || {};
            const built = entry.build(data, marker.id);
            if (!built || !built.label) return null;
            return {
                key: marker.id,
                tone: entry.tone,
                label: built.label,
                detail: built.detail || null,
                data,
            };
        })
        .filter(Boolean);
}

export function deriveComboStrengthChips(profile) {
    return mapMarkers(profile?.strengths, STRENGTH_META);
}

export function deriveComboRiskChips(profile) {
    return mapMarkers(profile?.risks, RISK_META);
}

export function deriveComboNotableChips(profile) {
    return mapMarkers(profile?.notable, NOTABLE_META);
}

// ---- Headline --------------------------------------------------------

const DENSITY_COPY = {
    light: 'a light',
    moderate: 'a moderate',
    dense: 'a dense',
};

const REDUNDANCY_COPY = {
    fragile: 'fragile redundancy',
    conditional: 'conditional redundancy',
    stable: 'stable redundancy',
    redundant: 'stacked redundancy',
};

/**
 * One-sentence description that pairs combo density, redundancy, and the
 * primary outcome — the "elevator pitch" version of the profile.
 */
export function deriveComboHeadline(profile) {
    if (!profile || !profile.count) return null;
    const densityCopy = DENSITY_COPY[profile.density];
    const redundancyCopy = REDUNDANCY_COPY[profile.redundancy];
    const primary = profile.primaryOutcome ? outcomeLabel(profile.primaryOutcome) : null;
    const winconPaths = Array.isArray(profile.distinctWinconOutcomes)
        ? profile.distinctWinconOutcomes.length
        : 0;

    const lede = densityCopy
        ? `Carries ${densityCopy} combo package`
        : 'Carries a combo package';

    const pieces = [lede];
    if (primary) pieces.push(`leaning on ${primary.toLowerCase()}`);
    if (redundancyCopy) pieces.push(`with ${redundancyCopy}`);
    if (winconPaths >= 2) pieces.push(`across ${winconPaths} wincon paths`);
    return `${pieces.join(' ')}.`;
}
